/**
 * `/chats/:conversationId?` — Honeyguide Chats (docs/chats.md), beside the old
 * Genie at `/genie`, which this page shares nothing with but the activity
 * panel.
 *
 * The conversation itself is `useHgConversation`: the socket, the transcript
 * fold, send and stop. This page is the frame around it — the list of chats,
 * starting one, renaming and archiving.
 *
 * A new chat is created by its first message, not by "New chat": that button
 * only clears the page, so an abandoned click leaves no empty conversation
 * behind in the list.
 */

import React, { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { AnimatePresence, motion } from 'motion/react'
import { ChevronDown } from 'lucide-react'

import {
    addChatPersona,
    createChat,
    extractQuotaError,
    listChatAdvisors,
    listChats,
    removeChatPersona,
    sendChatMessage,
    updateChat,
    type HgAdvisor,
    type HgConversation,
    type LlmQuotaError,
} from '../api/client'
import ChatComposer from '../components/chats/ChatComposer'
import PersonaPicker from '../components/chats/PersonaPicker'
import ChatTranscript from '../components/chats/ChatTranscript'
import ConversationList, { chatTitle } from '../components/chats/ConversationList'
import ProfilePicker from '../components/chats/ProfilePicker'
import { UserBubble } from '../components/chats/ChatItemView'
import { QuotaBanner } from '../components/QuotaBanner'
import { UserContext } from '../components/UserContext'
import { isTurnLive, newClientKey, useHgConversation } from '../hooks/useHgConversation'

const byActivity = (a: HgConversation, b: HgConversation) =>
    b.last_activity_at.localeCompare(a.last_activity_at) || b.id - a.id

const upsert = (list: HgConversation[], conversation: HgConversation): HgConversation[] =>
    [conversation, ...list.filter((c) => c.id !== conversation.id)].sort(byActivity)

/** How close to the bottom still counts as "following" the conversation. */
const FOLLOW_SLACK_PX = 120

const ChatsPage: React.FC = () => {
    const { conversationId: idParam } = useParams()
    const parsedId = idParam ? Number(idParam) : NaN
    const conversationId = Number.isInteger(parsedId) && parsedId > 0 ? parsedId : null
    const navigate = useNavigate()
    const { user } = useContext(UserContext)

    const [conversations, setConversations] = useState<HgConversation[]>([])
    const [showArchived, setShowArchived] = useState(false)
    const [listOpen, setListOpen] = useState(false)
    // The first message of a chat that does not exist yet.
    const [starting, setStarting] = useState<{ text: string; key: string } | null>(null)
    const [startQuota, setStartQuota] = useState<LlmQuotaError | null>(null)
    const [startError, setStartError] = useState<string | null>(null)

    const chat = useHgConversation(conversationId)
    const { transcript } = chat
    const conversation = transcript.conversation

    // -- Advisors -------------------------------------------------------------

    const [advisors, setAdvisors] = useState<HgAdvisor[]>([])
    // Picked before a new chat's first message, which is what creates it.
    const [draftPersonas, setDraftPersonas] = useState<number[]>([])
    useEffect(() => {
        let alive = true
        listChatAdvisors()
            .then((rows) => alive && setAdvisors(rows))
            .catch(() => alive && setAdvisors([]))
        return () => {
            alive = false
        }
    }, [])
    useEffect(() => setDraftPersonas([]), [conversationId])

    const chosenPersonas: HgAdvisor[] =
        conversationId != null
            ? conversation?.personas ?? []
            : draftPersonas
                  .map((id) => advisors.find((a) => a.persona_id === id))
                  .filter((a): a is HgAdvisor => a != null)

    const addPersona = async (personaId: number) => {
        if (conversationId == null) {
            setDraftPersonas((ids) => (ids.includes(personaId) ? ids : [...ids, personaId]))
            return
        }
        try {
            chat.setConversation(await addChatPersona(conversationId, personaId))
        } catch {
            /* the chip doesn't appear, which says so */
        }
    }
    const removePersona = async (personaId: number) => {
        if (conversationId == null) {
            setDraftPersonas((ids) => ids.filter((id) => id !== personaId))
            return
        }
        try {
            chat.setConversation(await removeChatPersona(conversationId, personaId))
        } catch {
            /* the chip stays, which says so */
        }
    }

    // -- The list -------------------------------------------------------------

    useEffect(() => {
        let alive = true
        listChats(showArchived ? 'archived' : 'active')
            .then((rows) => alive && setConversations([...rows].sort(byActivity)))
            .catch(() => alive && setConversations([]))
        return () => {
            alive = false
        }
    }, [showArchived])

    // The open chat's title and activity, as the socket reports them.
    useEffect(() => {
        if (!conversation) return
        const wanted = showArchived ? 'archived' : 'active'
        setConversations((list) =>
            conversation.status === wanted
                ? upsert(list, conversation)
                : list.filter((c) => c.id !== conversation.id),
        )
    }, [conversation, showArchived])

    const select = (id: number) => {
        setListOpen(false)
        navigate(`/chats/${id}`)
    }

    const startNew = () => {
        setListOpen(false)
        setStarting(null)
        setStartError(null)
        navigate('/chats')
    }

    const setStatus = async (id: number, status: 'active' | 'archived') => {
        try {
            await updateChat(id, { status })
            setConversations((list) => list.filter((c) => c.id !== id))
            if (status === 'archived' && id === conversationId) navigate('/chats')
        } catch {
            /* the row stays where it was, which says so */
        }
    }

    // -- Sending --------------------------------------------------------------

    const send = useCallback(
        async (text: string): Promise<boolean> => {
            if (conversationId != null) return chat.send(text)
            const key = newClientKey()
            setStarting({ text, key })
            setStartError(null)
            setStartQuota(null)
            try {
                const created = await createChat()
                for (const personaId of draftPersonas) await addChatPersona(created.id, personaId)
                await sendChatMessage(created.id, text, key)
                setShowArchived(false)
                setConversations((list) => upsert(list, created))
                navigate(`/chats/${created.id}`)
                return true
            } catch (err) {
                setStarting(null)
                const quota = extractQuotaError(err)
                if (quota) setStartQuota(quota)
                else setStartError("Couldn't send. Try again.")
                return false
            }
        },
        [conversationId, chat.send, navigate, draftPersonas],
    )

    // The first message stays on screen until the chat's own copy of it loads.
    useEffect(() => {
        if (starting && conversationId != null && transcript.items.length > 0) setStarting(null)
    }, [starting, conversationId, transcript.items.length])

    // -- Following the reply --------------------------------------------------

    const endRef = useRef<HTMLDivElement>(null)
    const followingRef = useRef(true)
    useEffect(() => {
        const onScroll = () => {
            const doc = document.documentElement
            followingRef.current = doc.scrollHeight - window.scrollY - window.innerHeight < FOLLOW_SLACK_PX
        }
        window.addEventListener('scroll', onScroll, { passive: true })
        return () => window.removeEventListener('scroll', onScroll)
    }, [])
    useEffect(() => {
        followingRef.current = true
    }, [conversationId])
    const streamedLength = Object.values(transcript.streaming).reduce((n, s) => n + s.text.length, 0)
    useLayoutEffect(() => {
        if (followingRef.current) endRef.current?.scrollIntoView({ block: 'end' })
    }, [transcript.items.length, transcript.pending.length, streamedLength, starting, chat.activity?.steps.length])

    // -- Renaming -------------------------------------------------------------

    const [editingTitle, setEditingTitle] = useState<string | null>(null)
    const saveTitle = async () => {
        const title = (editingTitle ?? '').trim()
        setEditingTitle(null)
        if (!conversation || !title || title === conversation.title) return
        try {
            const updated = await updateChat(conversation.id, { title })
            setConversations((list) => list.map((c) => (c.id === updated.id ? updated : c)))
        } catch {
            /* the old title stays, which says so */
        }
    }

    const notFound = transcript.error === 'not_found'
    const live = isTurnLive(transcript.turn)
    const quotaError = conversationId != null ? transcript.quotaError : startQuota
    const actionError = conversationId != null ? chat.actionError : startError
    const isEmpty =
        conversationId == null ? !starting : transcript.items.length === 0 && !starting && transcript.pending.length === 0

    const list = (
        <ConversationList
            conversations={conversations}
            activeId={conversationId}
            showArchived={showArchived}
            onToggleArchived={() => setShowArchived((v) => !v)}
            onSelect={select}
            onNew={startNew}
            onArchive={(id) => void setStatus(id, 'archived')}
            onRestore={(id) => void setStatus(id, 'active')}
        />
    )

    return (
        <div className="relative flex min-h-[calc(100dvh-6rem)] shell-fill-height px-4 sm:px-0 gap-6">
            <aside className="hidden md:block w-60 shrink-0 pt-4">
                <div className="sticky top-4">{list}</div>
            </aside>

            <section className="flex-1 min-w-0 flex flex-col max-w-3xl mx-auto w-full">
                {/* Below md the advisors and model picker take a row of their own;
                    beside the title they squeezed it to nothing. */}
                <header className="flex flex-wrap md:flex-nowrap items-center gap-x-3 gap-y-2 sm:pt-4 pb-3">
                    <div className="relative md:hidden">
                        <motion.button
                            type="button"
                            onClick={() => setListOpen((v) => !v)}
                            whileTap={{ scale: 0.9 }}
                            transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                            className="inline-flex items-center gap-1 text-sm text-gray-600 cursor-pointer"
                            aria-expanded={listOpen}
                        >
                            Chats
                            <motion.span
                                animate={{ rotate: listOpen ? 180 : 0 }}
                                transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                                className="inline-flex"
                            >
                                <ChevronDown className="w-4 h-4" />
                            </motion.span>
                        </motion.button>
                        <AnimatePresence>
                            {listOpen && (
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.95, y: -8 }}
                                    animate={{ opacity: 1, scale: 1, y: 0 }}
                                    exit={{ opacity: 0, scale: 0.95, y: -8 }}
                                    transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                                    style={{ transformOrigin: 'top left' }}
                                    className="absolute left-0 top-8 z-20 w-72 max-h-[70vh] overflow-y-auto rounded-lg bg-blush-light shadow-lg p-2"
                                >
                                    {list}
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>

                    <div className="flex-1 min-w-0">
                        {editingTitle != null ? (
                            <input
                                autoFocus
                                value={editingTitle}
                                maxLength={255}
                                onChange={(e) => setEditingTitle(e.target.value)}
                                onBlur={() => void saveTitle()}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') void saveTitle()
                                    if (e.key === 'Escape') setEditingTitle(null)
                                }}
                                aria-label="Chat name"
                                className="w-full text-2xl font-bold text-gray-800 bg-white/70 rounded px-1 focus:outline-none focus:ring-2 focus:ring-action"
                            />
                        ) : conversation ? (
                            <h1
                                className="text-2xl font-bold text-gray-800 truncate cursor-text"
                                title="Rename"
                                onClick={() => setEditingTitle(conversation.title ?? '')}
                                data-testid="chat-title"
                            >
                                {chatTitle(conversation)}
                            </h1>
                        ) : (
                            <h1 className="text-2xl font-bold text-gray-800">Chats</h1>
                        )}
                    </div>

                    <div className="flex flex-wrap items-center gap-2 w-full md:w-auto md:flex-nowrap">
                        <PersonaPicker
                            advisors={advisors}
                            chosen={chosenPersonas}
                            onAdd={(id) => void addPersona(id)}
                            onRemove={(id) => void removePersona(id)}
                        />
                        {user?.isAdmin && <ProfilePicker />}
                    </div>
                </header>

                {notFound ? (
                    <div className="flex-1 py-16 text-center text-gray-600">
                        This chat isn't here.{' '}
                        <Link to="/chats" className="text-amber-800 hover:text-amber-900">
                            Start a new one
                        </Link>
                    </div>
                ) : (
                    <>
                        <div className="flex-1 py-2">
                            {isEmpty && (
                                <p className="py-16 text-center text-gray-500">What's on your mind?</p>
                            )}
                            {conversationId != null && (
                                <ChatTranscript
                                    transcript={transcript}
                                    activity={chat.activity}
                                    onToggleActivity={chat.setActivityExpanded}
                                    onToggleTechnical={chat.setActivityTechnical}
                                    onDecide={chat.decide}
                                />
                            )}
                            {starting && <UserBubble key={`send:${starting.key}`} text={starting.text} pending />}
                            <div ref={endRef} />
                        </div>

                        <div className="sticky bottom-0 pb-4 pt-2 bg-blush-light shell-safe-bottom">
                            {quotaError && (
                                <QuotaBanner
                                    quotaError={quotaError}
                                    onDismiss={conversationId != null ? chat.clearQuotaError : () => setStartQuota(null)}
                                />
                            )}
                            {actionError && <div className="text-rose-600 text-sm mb-2">{actionError}</div>}
                            <ChatComposer
                                key={conversationId ?? 'new'}
                                onSend={send}
                                canStop={live}
                                stopping={chat.stopping}
                                onStop={() => void chat.stop()}
                                disabled={Boolean(starting)}
                                advisors={advisors}
                            />
                        </div>
                    </>
                )}
            </section>
        </div>
    )
}

export default ChatsPage
